/**
 * Frame of the Academy (design v2): a thin glass top bar (brand, sections on a computer,
 * preferences), a floating tab bar on phones, and a calm tinted wallpaper behind. During a sitting
 * the frame steps back (focus mode): no navigation, nothing to click away from the exam.
 */

import { useAcademyAgencyName, useAcademyMePartialUpdate, useAuthLogoutCreate } from "@agencyos/api-client";
import type { Language } from "@agencyos/i18n";
import { Backdrop, SegmentedControl } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ChartLine, ClipboardList, GraduationCap, History, House, LifeBuoy, LogOut, MessagesSquare, Mic, Route, Settings2, Swords, UserRound, Video } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { OfflineNotice } from "../components/AppShell";
import { ThemeSwitcher } from "../components/Preferences";
import { session, useSession } from "../lib/session";
import { ExamSwitcher, useIsIelts } from "./ielts/exam";
import { AlwaysInMyLanguage } from "./ielts/InMyLanguage";
import { MotherTonguePreference } from "./ielts/MotherTongue";
import { CookieConsent, LegalFooter } from "./sales/CookieConsent";
import { useUrgentMocks } from "./UrgentMocks";

/** FR/EN of the interface; also saved on the learner (language of the corrections). */
export function AcademyLanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const { accessToken } = useSession();
  const save = useAcademyMePartialUpdate();
  return (
    <SegmentedControl<Language>
      label={t("settings.language")}
      value={(i18n.resolvedLanguage ?? "fr") as Language}
      options={[
        { value: "fr", label: "FR" },
        { value: "en", label: "EN" },
      ]}
      onChange={(language) => {
        void i18n.changeLanguage(language);
        if (accessToken) save.mutate({ data: { interface_language: language } });
      }}
    />
  );
}

const NAV = [
  { to: "/academy/home", key: "home", Icon: House, label: "academy.nav.home" },
  { to: "/academy/progress", key: "progress", Icon: ChartLine, label: "academy.nav.progress" },
  { to: "/academy/history", key: "history", Icon: History, label: "academyHistory.nav" },
  { to: "/academy/mocks", key: "mocks", Icon: ClipboardList, label: "academy.nav.mocks" },
  { to: "/academy/challenge", key: "challenge", Icon: Swords, label: "academy.nav.challenge" },
  { to: "/academy/tutor", key: "tutor", Icon: MessagesSquare, label: "academyTutor.nav" },
  { to: "/academy/live", key: "live", Icon: Video, label: "academyLive.nav" },
] as const;

/** The course path comes right after the home, for every exam (D-105, D-107). */
const COURSE = { to: "/academy/course", key: "course", Icon: Route, label: "ielts.nav.course" } as const;

/**
 * Sections of the learner space: one list, drawn as pills in the top bar on a computer and as a
 * floating tab bar on a phone (short captions; the full name stays the accessible name).
 */
function AcademyNav() {
  const { t } = useTranslation();
  const urgent = useUrgentMocks().length;
  const ielts = useIsIelts();
  const items = [NAV[0], COURSE, ...NAV.slice(1)];
  return (
    <nav className={`ac-nav ac-nav--wide${ielts ? " ac-nav--ielts" : ""}`} aria-label={t("academy.nav.label")}>
      <ul>
        {items.map(({ to, key, Icon, label }) => (
          <li key={key}>
            <NavLink to={to} className={({ isActive }) => `ac-nav__link${isActive ? " ac-nav__link--on" : ""}`}>
              <span className="ac-nav__icon">
                <Icon aria-hidden size={18} strokeWidth={1.9} />
                {key === "home" && urgent > 0 && <span className="ac-nav__dot" aria-hidden="true" />}
              </span>
              <span className="ac-nav__full">{t(label)}</span>
              <span className="ac-nav__short" aria-hidden="true">
                {key === "course" ? t("ielts.nav.courseShort") : t(`academy.ui.navShort.${key}`)}
              </span>
              {key === "home" && urgent > 0 && (
                <span className="ac-nav__badge">
                  <span aria-hidden="true">{t("academy.urgent.badge")}</span>
                  <span className="visually-hidden">{t("academy.urgent.navBadge", { count: urgent })}</span>
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Preferences (language, theme) and sign-out, in a small glass popover. */
function PreferencesMenu({ onSignOut }: { onSignOut?: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onDown = (event: PointerEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  return (
    <div className="ac-prefs" ref={root}>
      <button
        ref={button}
        type="button"
        className="ac-icon-button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
      >
        <Settings2 aria-hidden size={18} strokeWidth={1.9} />
        <span className="visually-hidden">{t("academy.ui.preferences")}</span>
      </button>
      <div id={panelId} className="ac-prefs__panel ao-glass ao-glass--thick" hidden={!open}>
        <p className="ac-prefs__title">{t("academy.ui.preferences")}</p>
        <AcademyLanguageSwitcher />
        <ThemeSwitcher />
        {onSignOut && <MotherTonguePreference />}
        {onSignOut && <AlwaysInMyLanguage />}
        {onSignOut && (
          <Link className="ac-prefs__signout" to="/academy/oral">
            <Mic aria-hidden size={16} />
            <span>{t("coaching.title")}</span>
          </Link>
        )}
        {onSignOut && (
          <Link className="ac-prefs__signout" to="/academy/support">
            <LifeBuoy aria-hidden size={16} />
            <span>{t("support.me.title")}</span>
          </Link>
        )}
        {onSignOut && (
          <Link className="ac-prefs__signout" to="/academy/account">
            <UserRound aria-hidden size={16} />
            <span>{t("academySales.account.title")}</span>
          </Link>
        )}
        {onSignOut && (
          <button type="button" className="ac-prefs__signout" onClick={onSignOut}>
            <LogOut aria-hidden size={16} />
            <span>{t("auth.logout")}</span>
          </button>
        )}
      </div>
    </div>
  );
}

export function AcademyLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const agency = useAcademyAgencyName();
  const { accessToken } = useSession();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const logout = useAuthLogoutCreate();
  // During a sitting the frame stays minimal: nothing to click away from the exam.
  const inPlayer = /\/academy\/attempt\/[^/]+$/.test(pathname);
  const withNav = Boolean(accessToken) && !inPlayer;

  const signOut = () =>
    logout.mutate(
      { data: {} },
      {
        onSettled: () => {
          queryClient.clear();
          session.signOut();
        },
      },
    );

  return (
    <div className={`academy ac-app${inPlayer ? " ac-app--focus" : ""}${withNav ? " ac-app--nav" : ""}`}>
      <Backdrop intensity="calm" />
      <div className="ac-wallpaper" aria-hidden="true" />
      {!inPlayer && (
        <header className="ac-top">
          <div className="ac-top__inner">
            <Link to={accessToken ? "/academy/home" : "/academy"} className="ac-brand" aria-label={agency ? t("academy.brand", { agency }) : t("academy.brandAcademy")}>
              <span className="ac-brand__mark" aria-hidden="true">
                <GraduationCap size={18} strokeWidth={2} />
              </span>
              <span className="ac-brand__name">
                {agency ? <span className="ac-brand__agency">{`${t("academy.brandMain", { agency })} `}</span> : ""}<strong>{t("academy.brandAcademy")}</strong>
              </span>
            </Link>
            {withNav && <AcademyNav />}
            <div className="ac-top__tools">
              {accessToken && <ExamSwitcher />}
              <PreferencesMenu onSignOut={accessToken ? signOut : undefined} />
            </div>
          </div>
        </header>
      )}
      <div className="ac-notices">
        <OfflineNotice />
      </div>
      <main className="ac-main" key={pathname}>
        {children}
      </main>
      {!inPlayer && <LegalFooter />}
      {!inPlayer && <CookieConsent />}
    </div>
  );
}
